import { supabase } from '../supabase';
import type { Comment, CommentCategory, PaginatedResponse } from '@/types/database.types';

export async function getComments(snapId: string, page = 1, limit = 10): Promise<PaginatedResponse<Comment>> {
  const { data: { user } } = await supabase.auth.getUser();

  // Get total count
  const { count } = await supabase
    .from('comments')
    .select('*', { count: 'exact', head: true })
    .eq('snap_id', snapId);

  // Get paginated comments with user reactions and profile data
  const { data: comments, error } = await supabase
    .from('comments')
    .select(`
      *,
      user:user_profiles!inner(
        id,
        username,
        avatar_url,
        created_at
      ),
      reactions:comment_reactions(
        reaction,
        user_id
      )
    `)
    .eq('snap_id', snapId)
    .order('created_at', { ascending: false })
    .range((page - 1) * limit, page * limit - 1);

  if (error) {
    console.error('Error fetching comments:', error);
    throw new Error('Failed to fetch comments');
  }

  const formattedComments = comments.map(comment => {
    // Find the user's reaction for this comment
    const userReaction = comment.reactions?.find(r => r.user_id === user?.id)?.reaction || null;

    // Use the joined user data directly
    const userData = {
      id: comment.user_id,
      username: comment.user.username || 'Anonymous',
      avatar_url: comment.user.avatar_url || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=80&h=80&fit=crop&crop=faces',
      created_at: comment.user.created_at || comment.created_at
    };

    return {
      ...comment,
      user: userData,
      userReaction
    };
  });

  return {
    data: formattedComments,
    total: count || 0,
    page,
    limit
  };
}

export async function createComment(
  snapId: string,
  content: string,
  category: CommentCategory
): Promise<Comment> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  // Get user profile data first
  const { data: profile } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('id', user.id)
    .single();

  // Create the comment
  const { data, error } = await supabase
    .from('comments')
    .insert({
      snap_id: snapId,
      content,
      category,
      user_id: user.id
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating comment:', error);
    throw new Error('Failed to create comment');
  }

  return {
    ...data,
    user: {
      id: user.id,
      username: profile?.username || 'Anonymous',
      avatar_url: profile?.avatar_url || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=80&h=80&fit=crop&crop=faces',
      created_at: profile?.created_at || data.created_at
    },
    userReaction: null,
    likes_count: 0,
    dislikes_count: 0
  };
}

export async function deleteComment(commentId: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { error } = await supabase
    .from('comments')
    .delete()
    .eq('id', commentId)
    .eq('user_id', user.id);

  if (error) {
    console.error('Error deleting comment:', error);
    throw new Error('Failed to delete comment');
  }
}

export async function addReaction(
  commentId: string,
  reaction: 'like' | 'dislike'
): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { error } = await supabase.rpc('handle_comment_reaction', {
    p_comment_id: commentId,
    p_user_id: user.id,
    p_reaction: reaction
  });

  if (error) {
    console.error('Error updating reaction:', error);
    throw new Error('Failed to update reaction');
  }
}